import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import WarehouseService from '../../services/warehouse.service';
import { NumberTextField } from '../../components/common';
import EmployeeService from '../../services/employee.service';

const WarehouseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    name: '',
    code: '',
    isActive: true,
  });

  useEffect(() => {
    fetchEmployees();
    if (isEdit && id) {
      fetchWarehouse(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAll();
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchWarehouse = async (warehouseId: number) => {
    try {
      const response = await WarehouseService.getOne(warehouseId);
      const warehouse = response.data?.data?.attributes;
      if (warehouse) {
        setFormData({
          ...warehouse,
          manager: warehouse.manager?.data?.id,
        });
      }
    } catch (error) {
      console.error('Error fetching warehouse:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await WarehouseService.update(parseInt(id), formData);
      } else {
        await WarehouseService.create(formData);
      }
      navigate('/warehouses');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save warehouse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Warehouse' : 'Create Warehouse'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Warehouse Code"
                name="code"
                value={formData.code || ''}
                onChange={handleChange}
                disabled={isEdit}
              />
              <TextField
                fullWidth
                required
                label="Warehouse Name"
                name="name"
                value={formData.name || ''}
                onChange={handleChange}
              />
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Address"
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={formData.country || ''}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                label="Manager"
                name="manager"
                value={formData.manager || ''}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    manager: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              >
                <MenuItem value="">No Manager</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.attributes?.firstName} {emp.attributes?.lastName}
                  </MenuItem>
                ))}
              </TextField>
              <NumberTextField
                fullWidth
                label="Capacity"
                name="capacity"
                value={formData.capacity || ''}
                onChange={handleChange}
                inputProps={{ min: '0' }}
                allowDecimal={false}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive || false}
                  onChange={handleChange}
                  name="isActive"
                />
              }
              label="Active"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/warehouses')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default WarehouseForm;

